import { useState, useEffect } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { theme } from '../../theme';
import {
  LayoutDashboard, Settings2, UserCog, BarChart2, TrendingUp,
  Receipt, FileText, Users, UserCheck, Network, Building2,
  CalendarDays, Home, SlidersHorizontal, Clock, Timer,
  Mic2, Award, ArrowLeftRight, GraduationCap, Wallet,
  Calculator, Megaphone, Trophy, Monitor, Tag, Key,
  LifeBuoy, CheckSquare, Factory, UserPlus,
  Target, Zap, FolderKanban, Inbox,
  ChevronRight, FileBarChart, MapPin, FlaskConical, Fingerprint,
} from 'lucide-react';

// ─── Icon size constants ──────────────────────────────────────────────────────
const ICON_SIZE    = 16;
const ICON_SIZE_SM = 14;
const ICON_STROKE  = 1.75;

// ─── Self-service groups (shared across all roles) ────────────────────────────

const SS_GROUPS = [
  {
    type: 'group', label: 'Leave & Time', icon: CalendarDays, key: 'ss_leave',
    items: [
      { label: 'My Leave',                 path: '/employee/leave',                icon: CalendarDays },
      { label: 'My WFH',                   path: '/employee/wfh',                  icon: Home },
      { label: 'My Attendance',            path: '/employee/attendance',           icon: Clock },
      { label: 'Client Visit',             path: '/employee/client-visit',         icon: MapPin },
      { label: 'My Timesheet',             path: '/employee/timesheet',            icon: Timer },
      { label: 'Team Attendance Approval', path: '/hrms/team-attendance-approval', icon: CheckSquare },
    ],
  },
  {
    type: 'group', label: 'Assets & IT', icon: Monitor, key: 'ss_assets',
    items: [
      { label: 'My Assets',  path: '/employee/assets',  icon: Monitor },
      { label: 'My Tickets', path: '/employee/tickets', icon: LifeBuoy },
    ],
  },
  {
    type: 'group', label: 'Finance', icon: Wallet, key: 'ss_finance',
    items: [
      { label: 'My Claims',   path: '/employee/claims',   icon: Receipt },
      { label: 'My Payslips', path: '/employee/payslips', icon: FileText },
    ],
  },
  {
    type: 'group', label: 'Growth & Career', icon: Zap, key: 'ss_growth',
    items: [
      { label: 'My Performance', path: '/employee/performance', icon: BarChart2 },
      { label: 'My Training',    path: '/employee/training',    icon: GraduationCap },
      { label: 'Org Chart',      path: '/employee/org-chart',   icon: Network },
    ],
  },
];

// ─── Shared nav groups ────────────────────────────────────────────────────────

const HR_FULL_NAV = [
  {
    type: 'group', label: 'People', icon: Users, key: 'hr_people',
    items: [
      { label: 'Employees',            path: '/hrms/employees',            icon: Users },
      { label: 'Profile Verification', path: '/hrms/profile-verification', icon: UserCheck },
      { label: 'Org Chart',            path: '/hrms/org-chart',            icon: Network },
      { label: 'Departments',          path: '/hrms/departments',          icon: Building2 },
    ],
  },
  {
    type: 'group', label: 'Time & Leave', icon: CalendarDays, key: 'hr_time',
    items: [
      { label: 'Leave Management',  path: '/hrms/leave',           icon: CalendarDays },
      { label: 'WFH Management',    path: '/hrms/wfh',             icon: Home },
      { label: 'Client Visits',     path: '/hrms/client-visits',   icon: MapPin },
      { label: 'WFH Limit Setup',   path: '/hrms/wfh-limits',      icon: SlidersHorizontal },
      { label: 'Attendance',             path: '/hrms/attendance',            icon: Clock },
      { label: 'Biometric Attendance',   path: '/hrms/biometric-attendance',  icon: Fingerprint },
      { label: 'Attendance Approval',    path: '/hrms/attendance-approval',   icon: CheckSquare },
      { label: 'Timesheet Report',       path: '/hrms/timesheet-admin',       icon: Timer },
    ],
  },
  {
    type: 'group', label: 'Talent', icon: Target, key: 'hr_talent',
    items: [
      { label: 'Interviews', path: '/hrms/interviews', icon: Mic2 },
      { label: 'Promotions', path: '/hrms/promotions', icon: Award },
      { label: 'Transfers',  path: '/hrms/transfers',  icon: ArrowLeftRight },
    ],
  },
  {
    type: 'group', label: 'Performance & Learning', icon: BarChart2, key: 'hr_perf',
    items: [
      { label: 'Performance', path: '/hrms/performance', icon: BarChart2 },
      { label: 'Training',    path: '/hrms/training',    icon: GraduationCap },
    ],
  },
  {
    type: 'group', label: 'Compensation', icon: Wallet, key: 'hr_comp',
    items: [
      { label: 'Payroll',      path: '/hrms/payroll',      icon: Wallet },
      { label: 'Pay Fixation', path: '/hrms/pay-fixation', icon: Calculator },
    ],
  },
  {
    type: 'group', label: 'Engagement', icon: Megaphone, key: 'hr_engage',
    items: [
      { label: 'Announcements', path: '/hrms/announcements', icon: Megaphone },
      { label: 'Hall of Fame',  path: '/hrms/hall-of-fame',  icon: Trophy },
    ],
  },
];

const HR_EXEC_NAV = [
  {
    type: 'group', label: 'People', icon: Users, key: 'hre_people',
    items: [
      { label: 'Employees',   path: '/hrms/employees',   icon: Users },
      { label: 'Org Chart',   path: '/hrms/org-chart',   icon: Network },
      { label: 'Departments', path: '/hrms/departments', icon: Building2 },
    ],
  },
  {
    type: 'group', label: 'Time & Leave', icon: CalendarDays, key: 'hre_time',
    items: [
      { label: 'Leave Management', path: '/hrms/leave',          icon: CalendarDays },
      { label: 'WFH Management',   path: '/hrms/wfh',            icon: Home },
      { label: 'Client Visits',    path: '/hrms/client-visits',  icon: MapPin },
      { label: 'WFH Limit Setup',  path: '/hrms/wfh-limits',     icon: SlidersHorizontal },
      { label: 'Attendance',       path: '/hrms/attendance',     icon: Clock },
    ],
  },
  {
    type: 'group', label: 'Talent', icon: Target, key: 'hre_talent',
    items: [
      { label: 'Interviews', path: '/hrms/interviews', icon: Mic2 },
      { label: 'Transfers',  path: '/hrms/transfers',  icon: ArrowLeftRight },
    ],
  },
  {
    type: 'group', label: 'Performance & Learning', icon: BarChart2, key: 'hre_perf',
    items: [
      { label: 'Performance', path: '/hrms/performance', icon: BarChart2 },
      { label: 'Training',    path: '/hrms/training',    icon: GraduationCap },
    ],
  },
  {
    type: 'group', label: 'Engagement', icon: Megaphone, key: 'hre_engage',
    items: [
      { label: 'Announcements', path: '/hrms/announcements', icon: Megaphone },
      { label: 'Hall of Fame',  path: '/hrms/hall-of-fame',  icon: Trophy },
    ],
  },
];

const SS_NAV = {
  heading: 'My Self-Service',
  items: [
    { label: 'My Tasks',        path: '/my-tasks',        icon: CheckSquare },
    { label: 'Test Management', path: '/test-management', icon: FlaskConical },
    ...SS_GROUPS,
  ],
};

// ─── Navigation definition for every role ────────────────────────────────────

const navSections = {

  // ── Super Admin ──────────────────────────────────────────────────────────
  super_admin: [
    {
      heading: 'Admin',
      items: [
        { label: 'My Dashboard',     path: '/employee/dashboard', icon: LayoutDashboard },
        { label: 'System Dashboard', path: '/dashboard',          icon: Settings2 },
        { label: 'User Management',  path: '/admin/users',        icon: UserCog },
        { label: 'Projects',         path: '/projects',           icon: FolderKanban },
      ],
    },
    {
      heading: 'HRMS',
      items: HR_FULL_NAV,
    },
    {
      heading: 'IT',
      items: [
        {
          type: 'group', label: 'Asset Management', icon: Monitor, key: 'sa_it_assets',
          items: [
            { label: 'Assets',           path: '/it/assets',     icon: Monitor },
            { label: 'Asset Categories', path: '/it/categories', icon: Tag },
            { label: 'Licenses',         path: '/it/licenses',   icon: Key },
          ],
        },
        {
          type: 'group', label: 'Support', icon: LifeBuoy, key: 'sa_it_support',
          items: [
            { label: 'Tickets',  path: '/it/tickets',  icon: LifeBuoy },
            { label: 'My Queue', path: '/it/my-queue', icon: Inbox },
          ],
        },
      ],
    },
    {
      heading: 'Finance',
      items: [
        { label: 'Finance Dashboard', path: '/finance/dashboard', icon: BarChart2 },
        { label: 'Claims',            path: '/finance/claims',    icon: Receipt },
        { label: 'Monthly Reports',   path: '/finance/reports',   icon: FileBarChart },
      ],
    },
    {
      heading: 'Sales',
      items: [
        { label: 'Sales Dashboard', path: '/sales/dashboard', icon: TrendingUp },
        { label: 'Tenders & Bids',  path: '/sales/tenders',   icon: FileText },
        { label: 'Vendors / OEMs',  path: '/sales/vendors',   icon: Factory },
      ],
    },
    {
      heading: 'Office',
      items: [
        { label: 'Visitor Management', path: '/office/visitors', icon: UserPlus },
      ],
    },
    SS_NAV,
  ],

  // ── CEO ──────────────────────────────────────────────────────────────────
  ceo: [
    {
      heading: 'Executive',
      items: [
        { label: 'My Dashboard',      path: '/employee/dashboard', icon: LayoutDashboard },
        { label: 'CEO Dashboard',     path: '/ceo/dashboard',      icon: BarChart2 },
        { label: 'Projects',          path: '/projects',           icon: FolderKanban },
        { label: 'Finance Dashboard', path: '/finance/dashboard',  icon: Receipt },
        { label: 'Attendance',        path: '/hrms/attendance',    icon: Clock },
        { label: 'Leave Calendar',    path: '/hrms/leave',         icon: CalendarDays },
        { label: 'Org Chart',         path: '/hrms/org-chart',     icon: Network },
      ],
    },
    {
      heading: 'Sales',
      items: [
        { label: 'Tenders & Bids', path: '/sales/tenders', icon: FileText },
        { label: 'Vendors / OEMs', path: '/sales/vendors', icon: Factory },
      ],
    },
    SS_NAV,
  ],

  // ── CTO ──────────────────────────────────────────────────────────────────
  cto: [
    {
      heading: 'Executive',
      items: [
        { label: 'My Dashboard',  path: '/employee/dashboard', icon: LayoutDashboard },
        { label: 'CTO Dashboard', path: '/ceo/dashboard',      icon: BarChart2 },
        { label: 'Projects',      path: '/projects',           icon: FolderKanban },
        { label: 'Employees',     path: '/hrms/employees',     icon: Users },
      ],
    },
    {
      heading: 'Sales',
      items: [
        { label: 'Tenders & Bids', path: '/sales/tenders', icon: FileText },
        { label: 'Vendors / OEMs', path: '/sales/vendors', icon: Factory },
      ],
    },
    {
      heading: 'Team & IT',
      items: [
        { label: 'Leave Approvals', path: '/hrms/leave',   icon: CalendarDays },
        { label: 'WFH Approvals',   path: '/hrms/wfh',     icon: Home },
        { label: 'IT Assets',       path: '/it/assets',    icon: Monitor },
        { label: 'IT Tickets',      path: '/it/tickets',   icon: LifeBuoy },
      ],
    },
    SS_NAV,
  ],

  // ── CFO ──────────────────────────────────────────────────────────────────
  cfo: [
    {
      heading: 'Executive',
      items: [
        { label: 'My Dashboard',  path: '/employee/dashboard', icon: LayoutDashboard },
        { label: 'CFO Dashboard', path: '/ceo/dashboard',      icon: BarChart2 },
        { label: 'Projects',      path: '/projects',           icon: FolderKanban },
      ],
    },
    {
      heading: 'Finance & HR',
      items: [
        {
          type: 'group', label: 'Finance', icon: Wallet, key: 'cfo_finance',
          items: [
            { label: 'Finance Dashboard', path: '/finance/dashboard',  icon: BarChart2 },
            { label: 'Claims',            path: '/finance/claims',     icon: Receipt },
            { label: 'Monthly Reports',   path: '/finance/reports',    icon: FileBarChart },
            { label: 'Payroll',           path: '/hrms/payroll',       icon: Wallet },
            { label: 'Pay Fixation',      path: '/hrms/pay-fixation',  icon: Calculator },
          ],
        },
        {
          type: 'group', label: 'People & HR', icon: Users, key: 'cfo_hr',
          items: [
            { label: 'Employees',            path: '/hrms/employees',             icon: Users },
            { label: 'Profile Verification', path: '/hrms/profile-verification',  icon: UserCheck },
            { label: 'Org Chart',            path: '/hrms/org-chart',             icon: Network },
            { label: 'Departments',          path: '/hrms/departments',           icon: Building2 },
            { label: 'Attendance',             path: '/hrms/attendance',            icon: Clock },
            { label: 'Biometric Attendance',  path: '/hrms/biometric-attendance',  icon: Fingerprint },
            { label: 'Attendance Approval',   path: '/hrms/attendance-approval',   icon: CheckSquare },
            { label: 'Leave Management',     path: '/hrms/leave',                 icon: CalendarDays },
            { label: 'WFH Management',       path: '/hrms/wfh',                   icon: Home },
            { label: 'Timesheet Report',     path: '/hrms/timesheet-admin',       icon: Timer },
          ],
        },
        {
          type: 'group', label: 'IT Assets', icon: Monitor, key: 'cfo_it',
          items: [
            { label: 'Assets',           path: '/it/assets',     icon: Monitor },
            { label: 'Asset Categories', path: '/it/categories', icon: Tag },
            { label: 'Licenses',         path: '/it/licenses',   icon: Key },
            { label: 'Tickets',          path: '/it/tickets',    icon: LifeBuoy },
          ],
        },
      ],
    },
    {
      heading: 'Sales',
      items: [
        { label: 'Tenders & Bids', path: '/sales/tenders', icon: FileText },
        { label: 'Vendors / OEMs', path: '/sales/vendors', icon: Factory },
      ],
    },
    SS_NAV,
  ],

  // ── HR Head ──────────────────────────────────────────────────────────────
  hr_head: [
    {
      heading: 'HR Head',
      items: [
        { label: 'My Dashboard', path: '/employee/dashboard', icon: LayoutDashboard },
        { label: 'HR Dashboard', path: '/dashboard',          icon: BarChart2 },
        { label: 'Projects',     path: '/projects',           icon: FolderKanban },
        ...HR_FULL_NAV,
      ],
    },
    SS_NAV,
  ],

  // ── HR Admin ─────────────────────────────────────────────────────────────
  hr_admin: [
    {
      heading: 'HR',
      items: [
        { label: 'My Dashboard', path: '/employee/dashboard', icon: LayoutDashboard },
        { label: 'HR Dashboard', path: '/dashboard',          icon: BarChart2 },
        { label: 'Projects',     path: '/projects',           icon: FolderKanban },
        ...HR_FULL_NAV,
      ],
    },
    SS_NAV,
  ],

  // ── HR Executive ─────────────────────────────────────────────────────────
  hr_executive: [
    {
      heading: 'HR Executive',
      items: [
        { label: 'My Dashboard', path: '/employee/dashboard', icon: LayoutDashboard },
        { label: 'HR Dashboard', path: '/dashboard',          icon: BarChart2 },
        ...HR_EXEC_NAV,
      ],
    },
    SS_NAV,
  ],

  // ── IT Admin ─────────────────────────────────────────────────────────────
  it_admin: [
    {
      heading: 'IT Admin',
      items: [
        { label: 'My Dashboard', path: '/employee/dashboard', icon: LayoutDashboard },
        { label: 'IT Dashboard', path: '/dashboard',          icon: Monitor },
        { label: 'Projects',     path: '/projects',           icon: FolderKanban },
        {
          type: 'group', label: 'Asset Management', icon: Monitor, key: 'ita_assets',
          items: [
            { label: 'Assets',           path: '/it/assets',     icon: Monitor },
            { label: 'Asset Categories', path: '/it/categories', icon: Tag },
            { label: 'Licenses',         path: '/it/licenses',   icon: Key },
          ],
        },
        {
          type: 'group', label: 'Support', icon: LifeBuoy, key: 'ita_support',
          items: [
            { label: 'Tickets',  path: '/it/tickets',  icon: LifeBuoy },
            { label: 'My Queue', path: '/it/my-queue', icon: Inbox },
          ],
        },
      ],
    },
    SS_NAV,
  ],

  // ── IT Support ───────────────────────────────────────────────────────────
  it_support: [
    {
      heading: 'IT Support',
      items: [
        { label: 'My Dashboard', path: '/employee/dashboard', icon: LayoutDashboard },
        {
          type: 'group', label: 'Assets (View)', icon: Monitor, key: 'its_assets',
          items: [
            { label: 'Assets',           path: '/it/assets',     icon: Monitor },
            { label: 'Asset Categories', path: '/it/categories', icon: Tag },
            { label: 'Licenses',         path: '/it/licenses',   icon: Key },
          ],
        },
        {
          type: 'group', label: 'Support', icon: LifeBuoy, key: 'its_support',
          items: [
            { label: 'Tickets',  path: '/it/tickets',  icon: LifeBuoy },
            { label: 'My Queue', path: '/it/my-queue', icon: Inbox },
          ],
        },
      ],
    },
    SS_NAV,
  ],

  // ── Finance Executive ────────────────────────────────────────────────────
  finance_executive: [
    {
      heading: 'Finance',
      items: [
        { label: 'My Dashboard',      path: '/employee/dashboard', icon: LayoutDashboard },
        { label: 'Finance Dashboard', path: '/finance/dashboard',  icon: BarChart2 },
        { label: 'Claims',            path: '/finance/claims',     icon: Receipt },
        { label: 'Monthly Reports',   path: '/finance/reports',    icon: FileBarChart },
        { label: 'Projects',          path: '/projects',           icon: FolderKanban },
      ],
    },
    SS_NAV,
  ],

  // ── Finance Team ─────────────────────────────────────────────────────────
  finance_team: [
    {
      heading: 'Finance',
      items: [
        { label: 'My Dashboard',      path: '/employee/dashboard', icon: LayoutDashboard },
        { label: 'Finance Dashboard', path: '/finance/dashboard',  icon: BarChart2 },
        { label: 'Claims',            path: '/finance/claims',     icon: Receipt },
        { label: 'Projects',          path: '/projects',           icon: FolderKanban },
      ],
    },
    SS_NAV,
  ],

  // ── Finance Head ─────────────────────────────────────────────────────────
  finance_head: [
    {
      heading: 'Finance Head',
      items: [
        { label: 'My Dashboard',      path: '/employee/dashboard', icon: LayoutDashboard },
        { label: 'Finance Dashboard', path: '/finance/dashboard',  icon: BarChart2 },
        { label: 'Claims',            path: '/finance/claims',     icon: Receipt },
        { label: 'Monthly Reports',   path: '/finance/reports',    icon: FileBarChart },
        { label: 'Projects',          path: '/projects',           icon: FolderKanban },
        { label: 'Payroll',           path: '/hrms/payroll',       icon: Wallet },
        { label: 'Pay Fixation',      path: '/hrms/pay-fixation',  icon: Calculator },
        { label: 'Assets',            path: '/it/assets',          icon: Monitor },
      ],
    },
    SS_NAV,
  ],

  // ── Project Manager ──────────────────────────────────────────────────────
  project_manager: [
    {
      heading: 'Project Management',
      items: [
        { label: 'My Dashboard', path: '/employee/dashboard',  icon: LayoutDashboard },
        { label: 'Projects',     path: '/projects',            icon: FolderKanban },
        { label: 'Work Hours',   path: '/projects/work-hours', icon: Timer },
        { label: 'My Tasks',     path: '/my-tasks',            icon: CheckSquare },
        { label: 'Tickets',      path: '/employee/tickets',    icon: LifeBuoy },
      ],
    },
    {
      heading: 'My Self-Service',
      items: SS_GROUPS,
    },
  ],

  // ── Project Lead ─────────────────────────────────────────────────────────
  project_lead: [
    {
      heading: 'Projects',
      items: [
        { label: 'My Dashboard', path: '/employee/dashboard',  icon: LayoutDashboard },
        { label: 'Projects',     path: '/projects',            icon: FolderKanban },
        { label: 'Work Hours',   path: '/projects/work-hours', icon: Timer },
        { label: 'My Tasks',     path: '/my-tasks',            icon: CheckSquare },
        { label: 'Tickets',      path: '/employee/tickets',    icon: LifeBuoy },
      ],
    },
    {
      heading: 'My Self-Service',
      items: SS_GROUPS,
    },
  ],

  // ── Team Lead ────────────────────────────────────────────────────────────
  team_lead: [
    {
      heading: 'Team Lead',
      items: [
        { label: 'My Dashboard', path: '/employee/dashboard',  icon: LayoutDashboard },
        { label: 'Projects',     path: '/projects',            icon: FolderKanban },
        { label: 'Work Hours',   path: '/projects/work-hours', icon: Timer },
        { label: 'My Tasks',     path: '/my-tasks',            icon: CheckSquare },
        { label: 'Tickets',      path: '/employee/tickets',    icon: LifeBuoy },
      ],
    },
    {
      heading: 'Team Management',
      items: [
        { label: 'WFH Approvals',            path: '/hrms/wfh',                      icon: Home },
        { label: 'Team Attendance Approval',  path: '/hrms/team-attendance-approval', icon: CheckSquare },
      ],
    },
    {
      heading: 'My Self-Service',
      items: SS_GROUPS,
    },
  ],

  // ── Reporting Manager ────────────────────────────────────────────────────
  reporting_manager: [
    {
      heading: 'Navigation',
      items: [
        { label: 'My Dashboard',  path: '/employee/dashboard', icon: LayoutDashboard },
        { label: 'Projects',      path: '/projects',           icon: FolderKanban },
        { label: 'My Tasks',      path: '/my-tasks',           icon: CheckSquare },
        { label: 'WFH Approvals', path: '/hrms/wfh',          icon: Home },
        { label: 'Team Attendance Approval', path: '/hrms/team-attendance-approval', icon: CheckSquare },
      ],
    },
    {
      heading: 'My Self-Service',
      items: SS_GROUPS,
    },
  ],

  // ── Solution Architect ───────────────────────────────────────────────────
  solution_architect: [
    {
      heading: 'Solution Architect',
      items: [
        { label: 'My Dashboard', path: '/employee/dashboard',  icon: LayoutDashboard },
        { label: 'Projects',     path: '/projects',            icon: FolderKanban },
        { label: 'Work Hours',   path: '/projects/work-hours', icon: Timer },
        { label: 'My Tasks',     path: '/my-tasks',            icon: CheckSquare },
        { label: 'Tickets',      path: '/employee/tickets',    icon: LifeBuoy },
      ],
    },
    {
      heading: 'Team Management',
      items: [
        { label: 'WFH Approvals',            path: '/hrms/wfh',                      icon: Home },
        { label: 'Team Attendance Approval',  path: '/hrms/team-attendance-approval', icon: CheckSquare },
      ],
    },
    {
      heading: 'My Self-Service',
      items: SS_GROUPS,
    },
  ],

  // ── Dev Lead ─────────────────────────────────────────────────────────────
  dev_lead: [
    {
      heading: 'Dev Lead',
      items: [
        { label: 'My Dashboard', path: '/employee/dashboard',  icon: LayoutDashboard },
        { label: 'Projects',     path: '/projects',            icon: FolderKanban },
        { label: 'Work Hours',   path: '/projects/work-hours', icon: Timer },
        { label: 'My Tasks',     path: '/my-tasks',            icon: CheckSquare },
        { label: 'Tickets',      path: '/employee/tickets',    icon: LifeBuoy },
      ],
    },
    {
      heading: 'Team Management',
      items: [
        { label: 'WFH Approvals',            path: '/hrms/wfh',                      icon: Home },
        { label: 'Team Attendance Approval',  path: '/hrms/team-attendance-approval', icon: CheckSquare },
      ],
    },
    {
      heading: 'My Self-Service',
      items: SS_GROUPS,
    },
  ],

  // ── QA Lead ──────────────────────────────────────────────────────────────
  qa_lead: [
    {
      heading: 'QA Lead',
      items: [
        { label: 'My Dashboard', path: '/employee/dashboard',  icon: LayoutDashboard },
        { label: 'Projects',     path: '/projects',            icon: FolderKanban },
        { label: 'Work Hours',   path: '/projects/work-hours', icon: Timer },
        { label: 'My Tasks',     path: '/my-tasks',            icon: CheckSquare },
        { label: 'Tickets',      path: '/employee/tickets',    icon: LifeBuoy },
      ],
    },
    {
      heading: 'Team Management',
      items: [
        { label: 'WFH Approvals',            path: '/hrms/wfh',                      icon: Home },
        { label: 'Team Attendance Approval',  path: '/hrms/team-attendance-approval', icon: CheckSquare },
      ],
    },
    {
      heading: 'My Self-Service',
      items: SS_GROUPS,
    },
  ],

  // ── Developer ────────────────────────────────────────────────────────────
  developer: [
    {
      heading: 'Navigation',
      items: [
        { label: 'My Dashboard',    path: '/employee/dashboard', icon: LayoutDashboard },
        { label: 'Projects',        path: '/projects',           icon: FolderKanban },
        { label: 'My Tasks',        path: '/my-tasks',           icon: CheckSquare },
        { label: 'Tickets',         path: '/employee/tickets',   icon: LifeBuoy },
        { label: 'Test Management', path: '/test-management',    icon: FlaskConical },
        ...SS_GROUPS,
      ],
    },
  ],

  // ── Tester ───────────────────────────────────────────────────────────────
  tester: [
    {
      heading: 'Navigation',
      items: [
        { label: 'My Dashboard',    path: '/employee/dashboard', icon: LayoutDashboard },
        { label: 'Projects',        path: '/projects',           icon: FolderKanban },
        { label: 'My Tasks',        path: '/my-tasks',           icon: CheckSquare },
        { label: 'Tickets',         path: '/employee/tickets',   icon: LifeBuoy },
        { label: 'Test Management', path: '/test-management',    icon: FlaskConical },
        ...SS_GROUPS,
      ],
    },
  ],

  // ── Business Analyst ─────────────────────────────────────────────────────
  business_analyst: [
    {
      heading: 'Navigation',
      items: [
        { label: 'My Dashboard', path: '/employee/dashboard', icon: LayoutDashboard },
        { label: 'Projects',     path: '/projects',           icon: FolderKanban },
        { label: 'My Tasks',     path: '/my-tasks',           icon: CheckSquare },
        { label: 'Tickets',      path: '/employee/tickets',   icon: LifeBuoy },
        ...SS_GROUPS,
      ],
    },
  ],

  // ── Sales Head ───────────────────────────────────────────────────────────
  sales_head: [
    {
      heading: 'Sales',
      items: [
        { label: 'My Dashboard',    path: '/employee/dashboard', icon: LayoutDashboard },
        { label: 'Sales Dashboard', path: '/sales/dashboard',    icon: TrendingUp },
        { label: 'Tenders & Bids',  path: '/sales/tenders',      icon: FileText },
        { label: 'Vendors / OEMs',  path: '/sales/vendors',      icon: Factory },
      ],
    },
    SS_NAV,
  ],

  // ── Sales Executive ──────────────────────────────────────────────────────
  sales_executive: [
    {
      heading: 'Sales',
      items: [
        { label: 'My Dashboard',   path: '/employee/dashboard', icon: LayoutDashboard },
        { label: 'Tenders & Bids', path: '/sales/tenders',      icon: FileText },
        { label: 'Vendors / OEMs', path: '/sales/vendors',      icon: Factory },
      ],
    },
    SS_NAV,
  ],

  // ── Office Admin ─────────────────────────────────────────────────────────
  office_admin: [
    {
      heading: 'Office Administration',
      items: [
        { label: 'My Dashboard',       path: '/employee/dashboard', icon: LayoutDashboard },
        { label: 'Visitor Management', path: '/office/visitors',    icon: UserPlus },
      ],
    },
    SS_NAV,
  ],

  // ── Employee ─────────────────────────────────────────────────────────────
  employee: [
    {
      heading: 'Navigation',
      items: [
        { label: 'My Dashboard',    path: '/employee/dashboard', icon: LayoutDashboard },
        { label: 'Projects',        path: '/projects',           icon: FolderKanban },
        { label: 'My Tasks',        path: '/my-tasks',           icon: CheckSquare },
        { label: 'Test Management', path: '/test-management',    icon: FlaskConical },
        ...SS_GROUPS,
      ],
    },
  ],
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function getGroupsWithActiveChild(sections, pathname) {
  const active = new Set();
  for (const section of sections) {
    for (const item of section.items) {
      if (item.type === 'group') {
        const hasActive = item.items.some(child => {
          const clean = child.path.split('?')[0];
          return pathname === clean || pathname.startsWith(clean + '/');
        });
        if (hasActive) active.add(item.key);
      }
    }
  }
  return active;
}

function isPathActive(path, pathname) {
  const clean = path.split('?')[0];
  if (clean === '/dashboard' || clean === '/employee/dashboard') return pathname === clean;
  return pathname === clean || pathname.startsWith(clean + '/');
}

// ─── SidebarItem ─────────────────────────────────────────────────────────────

function SidebarItem({ item, isActive, indent }) {
  const [hovered, setHovered] = useState(false);
  const IconComponent = item.icon;
  return (
    <Link
      to={item.path}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '0.65rem',
        padding: indent ? '0.5rem 1rem 0.5rem 2.25rem' : '0.6rem 1.25rem',
        borderRadius: '0.5rem',
        margin: '0.05rem 0.5rem',
        color: isActive ? theme.white : hovered ? theme.white : theme.sidebarText,
        background: isActive ? theme.primary : hovered ? 'rgba(255,255,255,0.07)' : 'transparent',
        fontWeight: isActive ? 600 : 400,
        fontSize: indent ? '0.82rem' : '0.875rem',
        transition: 'all 0.15s ease',
        textDecoration: 'none',
        borderLeft: isActive ? `3px solid ${theme.white}` : '3px solid transparent',
      }}
    >
      {IconComponent && (
        <IconComponent
          size={indent ? ICON_SIZE_SM : ICON_SIZE}
          strokeWidth={ICON_STROKE}
          style={{ flexShrink: 0, opacity: indent ? 0.85 : 1 }}
        />
      )}
      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.label}</span>
    </Link>
  );
}

// ─── SidebarGroup ─────────────────────────────────────────────────────────────

function SidebarGroup({ group, isOpen, onToggle, isActiveChild }) {
  const [hovered, setHovered] = useState(false);
  const IconComponent = group.icon;
  return (
    <div>
      <button
        onClick={onToggle}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        style={{
          display: 'flex', alignItems: 'center', gap: '0.65rem',
          width: 'calc(100% - 1rem)', padding: '0.6rem 1rem 0.6rem 1.25rem',
          borderRadius: '0.5rem', margin: '0.05rem 0.5rem',
          background: isActiveChild && !isOpen ? 'rgba(249,115,22,0.12)' : hovered ? 'rgba(255,255,255,0.06)' : 'transparent',
          border: 'none', cursor: 'pointer',
          color: isActiveChild ? theme.primary : theme.sidebarText,
          fontWeight: isActiveChild ? 600 : 400,
          fontSize: '0.875rem', textAlign: 'left',
          transition: 'all 0.15s ease',
          borderLeft: `3px solid ${isActiveChild && !isOpen ? theme.primary : 'transparent'}`,
          boxSizing: 'border-box',
        }}
      >
        {IconComponent && (
          <IconComponent size={ICON_SIZE} strokeWidth={ICON_STROKE} style={{ flexShrink: 0 }} />
        )}
        <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{group.label}</span>
        <ChevronRight
          size={12}
          strokeWidth={2}
          style={{
            color: isActiveChild ? theme.primary : '#4B5563',
            transition: 'transform 0.2s ease',
            transform: isOpen ? 'rotate(90deg)' : 'rotate(0deg)',
            marginRight: '0.25rem',
            flexShrink: 0,
          }}
        />
      </button>

      {isOpen && (
        <div style={{
          borderLeft: `2px solid rgba(249,115,22,0.25)`,
          marginLeft: '1.75rem',
          marginRight: '0.5rem',
          marginBottom: '0.15rem',
        }}>
          {group.items.map((child, idx) => (
            <SidebarItem key={idx} item={child} isActive={isPathActive(child.path, window.location.pathname)} indent />
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Sidebar ──────────────────────────────────────────────────────────────────

function getSections(user) {
  return navSections[user?.role] || navSections.employee;
}

function Sidebar({ user, collapsed, onToggle }) {
  const location = useLocation();
  const sections = getSections(user);

  const [openGroups, setOpenGroups] = useState(() => {
    return getGroupsWithActiveChild(sections, location.pathname);
  });

  useEffect(() => {
    const autoOpen = getGroupsWithActiveChild(sections, location.pathname);
    setOpenGroups(prev => {
      const next = new Set(prev);
      autoOpen.forEach(k => next.add(k));
      return next;
    });
  }, [location.pathname]); // eslint-disable-line

  const toggleGroup = (key) => {
    setOpenGroups(prev => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  const isActive = (path) => isPathActive(path, location.pathname);

  const hasActiveChild = (group) =>
    group.items.some(child => isPathActive(child.path, location.pathname));

  return (
    <>
      {!collapsed && (
        <div onClick={onToggle} style={{ display: 'none', position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 49 }} className="mobile-overlay" />
      )}

      <aside style={{
        width: collapsed ? '0' : '240px',
        minWidth: collapsed ? '0' : '240px',
        background: theme.sidebarBg,
        height: '100%',
        overflowY: 'auto',
        overflowX: 'hidden',
        transition: 'width 0.25s ease, min-width 0.25s ease',
        flexShrink: 0,
        borderRight: '1px solid rgba(255,255,255,0.05)',
      }}>
        {!collapsed && (
          <div style={{ paddingBottom: '1.5rem' }}>
            {sections.map((section, si) => (
              <div key={si}>
                <div style={{ padding: si === 0 ? '1rem 1.25rem 0.5rem' : '0.75rem 1.25rem 0.5rem', marginTop: si > 0 ? '0.25rem' : 0 }}>
                  {si > 0 && <div style={{ borderTop: '1px solid rgba(255,255,255,0.07)', marginBottom: '0.75rem' }} />}
                  <span style={{ fontSize: '0.62rem', textTransform: 'uppercase', letterSpacing: '0.1em', color: '#6B7280', fontWeight: 700 }}>
                    {section.heading}
                  </span>
                </div>

                {section.items.map((item, idx) =>
                  item.type === 'group' ? (
                    <SidebarGroup
                      key={idx}
                      group={item}
                      isOpen={openGroups.has(item.key)}
                      onToggle={() => toggleGroup(item.key)}
                      isActiveChild={hasActiveChild(item)}
                    />
                  ) : (
                    <SidebarItem key={idx} item={item} isActive={isActive(item.path)} />
                  )
                )}
              </div>
            ))}

            <div style={{ borderTop: '1px solid rgba(255,255,255,0.07)', margin: '1rem 0.75rem 0', paddingTop: '1rem' }}>
              <p style={{ fontSize: '0.7rem', color: '#4B5563', textAlign: 'center', margin: 0 }}>Bonton Suite</p>
              <p style={{ fontSize: '0.62rem', color: '#374151', textAlign: 'center', margin: '0.2rem 0 0' }}>v1.0.0</p>
            </div>
          </div>
        )}
      </aside>
    </>
  );
}

export default Sidebar;
